import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';

const LeverancierSpotlight = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const featuredLeveranciers = [
    {
      id: 1,
      name: "SolarTech Nederland",
      logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=1",
      rating: 4.8,
      reviewCount: 342,
      specialties: ["Zonnepanelen", "Thuisbatterijen"],
      serviceArea: "Noord-Holland, Utrecht",
      description: "Specialist in hoogwaardige zonnepanelen en energieopslagsystemen. Meer dan 15 jaar ervaring in duurzame energie.",
      completedProjects: 1250,
      averagePrice: "€9.500",
      responseTime: "< 2 uur",
      certifications: ["RVO", "ISSO", "KvK"],
      isVerified: true,
      image: "https://images.pexels.com/photos/9875441/pexels-photo-9875441.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1"
    },
    {
      id: 2,
      name: "WarmtePomp Experts",
      logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=1",
      rating: 4.9,
      reviewCount: 198,
      specialties: ["Warmtepompen", "Isolatie"],
      serviceArea: "Zuid-Holland, Zeeland",
      description: "Gecertificeerde installateurs van warmtepompen en isolatiesystemen. Persoonlijke begeleiding van A tot Z.",
      completedProjects: 890,
      averagePrice: "€16.500",
      responseTime: "< 1 uur",
      certifications: ["ISSO", "KvK", "Kiwa"],
      isVerified: true,
      image: "https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1"
    },
    {
      id: 3,
      name: "EcoHome Solutions",
      logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=1",
      rating: 4.7,
      reviewCount: 276,
      specialties: ["Isolatie", "Waterontharders", "Laadpalen"],
      serviceArea: "Gelderland, Overijssel",
      description: "Complete duurzaamheidsoplossingen voor uw woning. Van isolatie tot laadpalen, alles onder één dak.",
      completedProjects: 1580,
      averagePrice: "€4.200",
      responseTime: "< 3 uur",
      certifications: ["RVO", "KvK", "ISSO"],
      isVerified: true,
      image: "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1"
    }
  ];

  const nextLeverancier = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredLeveranciers?.length);
  };

  const prevLeverancier = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredLeveranciers?.length) % featuredLeveranciers?.length);
  };

  const currentLeverancier = featuredLeveranciers?.[currentIndex];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hover: {
      scale: 1.1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const statVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background relative overflow-hidden">
      {/* Animated background */}
      <motion.div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)"
        }}
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-8 sm:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="ShieldCheck" size={24} className="text-success" />
            <span className="text-sm font-caption font-medium text-success">
              Geselecteerde Aanbieders
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Kwaliteit Boven Kwantiteit
          </h2>
          <p className="text-lg font-body text-muted-foreground max-w-3xl mx-auto">
            Alle aanbieders op ons platform zijn zorgvuldig geselecteerd op basis van certificeringen, 
            klanttevredenheid en vakmanschap
          </p>
        </motion.div>

        {/* Selection Criteria */}
        <motion.div 
          className="bg-card border border-border rounded-lg p-6 mb-12 max-w-4xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4 text-center">
            Onze Selectiecriteria
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={16} className="text-primary flex-shrink-0" />
              <span className="text-sm font-body text-muted-foreground">Erkende certificeringen</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Star" size={16} className="text-primary flex-shrink-0" />
              <span className="text-sm font-body text-muted-foreground">Minimaal 4.5 sterren</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0" />
              <span className="text-sm font-body text-muted-foreground">Geverifieerde projecten</span>
            </div>
          </div>
        </motion.div>

        {/* Leverancier Spotlight Card */}
        <motion.div 
          className="relative"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          key={currentIndex}
        >
          <motion.div 
            className="bg-card border border-border rounded-2xl overflow-hidden shadow-[0_12px_32px_-8px_rgba(0,0,0,0.15)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.25)] transition-all duration-500 transform perspective-1000"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            whileHover={{ 
              y: -8,
              scale: 1.01,
              boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.25), 0 0 15px rgba(59, 130, 246, 0.1)"
            }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Leverancier Image */}
              <div className="relative aspect-[4/3] lg:aspect-auto overflow-hidden">
                <motion.div
                  variants={imageVariants}
                  whileHover="hover"
                >
                  <Image
                    src={currentLeverancier?.image}
                    alt={`${currentLeverancier?.name} project`}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent lg:hidden"></div>
              </div>

              {/* Leverancier Info */}
              <div className="p-6 lg:p-8">
                {/* Header */}
                <motion.div 
                  className="flex items-start justify-between mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Image
                        src={currentLeverancier?.logo}
                        alt={`${currentLeverancier?.name} logo`}
                        className="w-16 h-16 rounded-lg object-cover border-2 border-border"
                      />
                      {currentLeverancier?.isVerified && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-success rounded-full flex items-center justify-center">
                          <Icon name="Check" size={12} color="white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-foreground mb-1">
                        {currentLeverancier?.name}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <Icon name="Star" size={16} className="text-yellow-500 fill-current" />
                          <span className="text-sm font-body font-medium text-foreground">
                            {currentLeverancier?.rating}
                          </span>
                        </div>
                        <span className="text-sm font-caption text-muted-foreground">
                          ({currentLeverancier?.reviewCount} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Description */}
                <p className="text-sm font-body text-muted-foreground mb-6">
                  {currentLeverancier?.description}
                </p>

                {/* Specialties */}
                <div className="mb-6">
                  <h4 className="text-sm font-heading font-medium text-foreground mb-2">
                    Specialisaties:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {currentLeverancier?.specialties?.map((specialty, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary text-xs font-caption rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Stats Grid */}
                <motion.div 
                  className="grid grid-cols-2 gap-4 mb-6"
                  variants={containerVariants}
                >
                  <motion.div 
                    className="text-center p-3 bg-muted/50 rounded-lg"
                    variants={statVariants}
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(var(--muted-rgb, 240, 240, 240), 0.8)" }}
                  >
                    <div className="text-lg font-heading font-semibold text-foreground">
                      {currentLeverancier?.completedProjects}
                    </div>
                    <div className="text-xs font-caption text-muted-foreground">
                      Projecten voltooid
                    </div>
                  </motion.div>
                  <motion.div 
                    className="text-center p-3 bg-muted/50 rounded-lg"
                    variants={statVariants}
                    whileHover={{ scale: 1.05, backgroundColor: "rgba(var(--muted-rgb, 240, 240, 240), 0.8)" }}
                  >
                    <div className="text-lg font-heading font-semibold text-foreground">
                      {currentLeverancier?.responseTime}
                    </div>
                    <div className="text-xs font-caption text-muted-foreground">
                      Reactietijd
                    </div>
                  </motion.div>
                </motion.div>

                {/* Service Area & Price */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-body text-muted-foreground">
                      {currentLeverancier?.serviceArea}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Euro" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-body text-muted-foreground">
                      Gemiddelde prijs: <span className="font-medium text-foreground">{currentLeverancier?.averagePrice}</span>
                    </span>
                  </div>
                </div>

                {/* Certifications */}
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span className="text-sm font-heading font-medium text-foreground">
                      Certificeringen:
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    {currentLeverancier?.certifications?.map((cert, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-success/10 text-success text-xs font-caption rounded border border-success/20"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button iconName="MessageCircle" iconPosition="left" fullWidth>
                    Gratis offerte
                  </Button>
                  <Link to="/leveranciers" className="flex-1">
                    <Button variant="outline" iconName="Eye" iconPosition="left" fullWidth>
                      Bekijk profiel
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Arrows */}
          <motion.button
            onClick={prevLeverancier}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center shadow-elevation-2 hover:bg-primary hover:text-primary-foreground transition-all z-10"
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
          >
            <Icon name="ChevronLeft" size={24} />
          </motion.button>
          <motion.button
            onClick={nextLeverancier}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center shadow-elevation-2 hover:bg-primary hover:text-primary-foreground transition-all z-10"
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.9 }}
          >
            <Icon name="ChevronRight" size={24} />
          </motion.button>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 mt-6">
            {featuredLeveranciers?.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex ? "w-8 bg-primary" : "w-2 bg-border"
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </motion.div>

        {/* View All CTA */}
        <motion.div 
          className="text-center mt-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <Link to="/leveranciers">
            <Button size="lg" iconName="Users" iconPosition="left">
              Bekijk alle aanbieders
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default LeverancierSpotlight;